'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ThemeEmailArchiveShell } from './ThemeEmailArchiveShell';

type EmailArchivePreviewContextValue = {
  open: (args: { s3Key: string }) => void;
};

const EmailArchivePreviewContext = createContext<EmailArchivePreviewContextValue | null>(null);

/**
 * In-app mailbox preview. Mount inside the app QueryClient and FeaturesContext.
 * ``open`` is null when the provider is absent, so a preview button can fall
 * back to the standalone tab.
 */
export function useEmailArchivePreview(): EmailArchivePreviewContextValue | null {
  return useContext(EmailArchivePreviewContext);
}

export function EmailArchivePreviewProvider({ children }: { children: ReactNode }) {
  const [s3Key, setS3Key] = useState<string | null>(null);
  const open = useCallback((args: { s3Key: string }) => {
    setS3Key(args.s3Key);
  }, []);
  const close = useCallback(() => setS3Key(null), []);
  const value = useMemo(() => ({ open }), [open]);

  return (
    <EmailArchivePreviewContext.Provider value={value}>
      {children}
      {s3Key ? (
        <ThemeEmailArchiveShell s3Key={s3Key} variant="modal" isOpen onClose={close} />
      ) : null}
    </EmailArchivePreviewContext.Provider>
  );
}
